// Tab Mức độ đọc: tài liệu ban hành trong kỳ được đọc nhanh/chậm thế nào, phòng ban × loại nào đọc kém,
// tài liệu và nhân sự nào cần nhắc — kèm lượt xem/tải theo thời gian.
import { Button, Table, Tooltip, message } from 'antd';
import { Link } from 'react-router-dom';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { BellRing } from 'lucide-react';
import { deptName } from '../data';
import { READ_BUCKETS, type accessSeries, type readingAnalytics } from '../analytics';
import useDocsStore from '../store';
import { docLink, useDocAuth } from '../utils';
import { COLORS, Card, Legend, RateBar, Stat, StatGrid, ThinDataHint, fmtDays, pct, rateColor, type DashCtx } from './ui';

const BUCKET_COLORS = [COLORS.green, '#5DCAA5', COLORS.amber, COLORS.coral, '#CBD5E1'];

export default function ReadingTab({ ctx, data, access }: { ctx: DashCtx; data: ReturnType<typeof readingAnalytics>; access: ReturnType<typeof accessSeries> }) {
  const { me } = useDocAuth();
  const remindReaders = useDocsStore(s => s.remindReaders);
  const remind = (docId: string, n: number) => {
    remindReaders(docId, me, n);
    message.success(`Đã gửi nhắc đọc tới ${n} người (mô phỏng email / thông báo)`);
  };
  const typeName = (id: string) => ctx.types.find(t => t.id === id)?.name || '—';

  return (
    <>
      <ThinDataHint count={data.cohort} ctx={ctx} what="tài liệu ban hành trong kỳ" />
      <StatGrid>
        <Stat label="Tỷ lệ đã đọc" value={pct(data.rate)} tone={data.rate >= 0.7 ? 'text-emerald-600' : data.rate >= 0.5 ? 'text-amber-600' : 'text-red-500'}
          sub={`${data.read}/${data.pairs} lượt · ${data.cohort} tài liệu ban hành trong kỳ`} info="Tính trên từng cặp tài liệu × người được chia sẻ, chỉ gồm tài liệu ban hành trong kỳ." />
        <Stat label="Đọc trong 24 giờ" value={pct(data.within24)} sub="trên tổng lượt được chia sẻ" />
        <Stat label="Thời gian đọc trung vị" value={data.read ? fmtDays(data.medianDays) : '—'} sub="từ lúc ban hành đến lúc mở" />
        <Stat label="Lượt chưa đọc" value={data.unread} tone={data.unread ? 'text-red-500' : 'text-slate-800'} sub={`${data.topPeople.length ? `${data.topPeople.length}+ nhân sự còn tồn` : 'Không có tồn đọng'}`} />
        <Stat label="Lượt tải xuống" value={data.downloads} sub="trong kỳ, mọi tài liệu" />
      </StatGrid>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Thời gian từ ban hành đến lúc đọc" info="Mỗi cột là số lượt (tài liệu × người nhận) theo khoảng thời gian đọc.">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.buckets} margin={{ top: 16, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={0} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <ChartTooltip formatter={v => [`${v} lượt (${data.pairs ? Math.round((Number(v) / data.pairs) * 100) : 0}%)`, 'Số lượt']} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={36} label={{ position: 'top', fontSize: 11, fill: '#64748b' }}>
                  {data.buckets.map((b, i) => <Cell key={b.label} fill={BUCKET_COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Đường đọc tích lũy" hint="% lượt đã đọc sau N ngày" info="Sau N ngày kể từ khi ban hành, bao nhiêu phần trăm người được chia sẻ đã mở tài liệu.">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.curve} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} ticks={[0, 1, 3, 7, 14, 21, 30]} tickFormatter={d => `${d}n`} />
                <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
                <ChartTooltip labelFormatter={d => `Sau ${d} ngày`} formatter={v => [`${v}%`, 'Đã đọc']} />
                <ReferenceLine x={7} stroke={COLORS.amber} strokeDasharray="4 4" label={{ value: '7 ngày', position: 'insideTopRight', fontSize: 10, fill: '#BA7517' }} />
                <Area dataKey="pct" stroke={COLORS.brand} fill={COLORS.brand} fillOpacity={0.12} strokeWidth={2} type="monotone" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card title="Ma trận tỷ lệ đọc: phòng ban × loại tài liệu" hint="ô trống = không có tài liệu chia sẻ" className="mb-4">
        {data.matrix.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Chưa có dữ liệu trong kỳ</div> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-separate border-spacing-1 text-[13px]">
              <thead>
                <tr>
                  <th className="w-40 text-left font-medium text-slate-500">Phòng ban</th>
                  {data.usedTypes.map(t => <th key={t.id} className="font-medium text-slate-500">{t.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.matrix.map(r => (
                  <tr key={r.dept.id}>
                    <td className="truncate text-slate-600">{r.dept.name}</td>
                    {r.cells.map(c => {
                      const rate = c.total ? c.read / c.total : 0;
                      return (
                        <td key={c.typeId} className="p-0">
                          {c.total ? (
                            <Tooltip title={`${r.dept.name} · ${typeName(c.typeId)}: ${c.read}/${c.total} lượt đã đọc`}>
                              <div className="rounded-md py-2 text-center font-semibold text-white" style={{ background: rateColor(rate), opacity: 0.45 + rate * 0.55 }}>{pct(rate)}</div>
                            </Tooltip>
                          ) : <div className="rounded-md bg-slate-50 py-2 text-center text-slate-300">—</div>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Tài liệu ít người đọc nhất" hint="tối thiểu 3 người nhận">
          <Table
            size="small" rowKey={r => r.doc.id} dataSource={data.lowDocs} pagination={false} scroll={{ x: 560 }}
            locale={{ emptyText: 'Mọi tài liệu đều đã được đọc đầy đủ' }}
            columns={[
              { title: 'Tài liệu', key: 'doc', render: (_v, r) => <div className="min-w-0"><Link to={docLink(r.doc.id)} className="font-medium text-[#357f96] hover:underline">{r.doc.code}</Link><div className="truncate text-xs text-slate-500">{r.doc.title}</div></div> },
              { title: 'Đã đọc', key: 'rate', width: 130, render: (_v, r) => <div><div className="mb-1 text-xs"><b>{pct(r.rate)}</b> <span className="text-slate-400">({r.read}/{r.total})</span></div><RateBar value={r.rate} color={rateColor(r.rate)} height={6} /></div> },
              { title: 'Ban hành', key: 'age', width: 90, render: (_v, r) => <span className="text-xs text-slate-500">{r.ageDays} ngày trước</span> },
              { title: '', key: 'act', width: 100, align: 'right', render: (_v, r) => <Button size="small" icon={<BellRing size={13} />} onClick={() => remind(r.doc.id, r.unread)}>Nhắc {r.unread}</Button> },
            ]}
          />
        </Card>
        <Card title="Nhân sự còn nhiều tài liệu chưa đọc">
          <Table
            size="small" rowKey={r => r.person.id} dataSource={data.topPeople} pagination={false} scroll={{ x: 480 }}
            locale={{ emptyText: 'Không có nhân sự tồn tài liệu chưa đọc' }}
            columns={[
              { title: 'Nhân sự', key: 'p', render: (_v, r) => <div><div className="font-medium text-slate-700">{r.person.name}</div><div className="text-xs text-slate-400">{deptName(r.person.deptId)} · {r.person.title}</div></div> },
              { title: 'Chưa đọc', key: 'n', width: 110, render: (_v, r) => <span><b className="text-red-500">{r.unread.length}</b><span className="text-xs text-slate-400"> / {r.total}</span></span> },
              { title: 'Lâu nhất', key: 'old', width: 100, render: (_v, r) => <span className={`text-xs ${r.oldestDays > 7 ? 'font-medium text-red-500' : 'text-slate-500'}`}>{r.oldestDays} ngày</span> },
            ]}
          />
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Lượt xem và tải xuống" hint={`theo ${access.unit === 'day' ? 'ngày' : access.unit === 'week' ? 'tuần' : 'tháng'}`} className="lg:col-span-2">
          <Legend items={[{ label: 'Lượt xem', color: COLORS.brand, line: true }, { label: 'Lượt tải', color: COLORS.violet, dashed: true }]} />
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={access.series} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} minTickGap={16} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                <ChartTooltip formatter={(v, n) => [v, n === 'views' ? 'Lượt xem' : 'Lượt tải']} />
                <Line dataKey="views" stroke={COLORS.brand} strokeWidth={2} dot={false} type="monotone" />
                <Line dataKey="downloads" stroke={COLORS.violet} strokeWidth={2} strokeDasharray="5 4" dot={false} type="monotone" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Xem nhiều nhất trong kỳ">
          {access.top.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Chưa có lượt xem trong kỳ</div> : (
            <ol className="space-y-2.5">
              {access.top.map((r, i) => (
                <li key={r.doc.id} className="flex items-start gap-2.5 text-[13px]">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ecf6f9] text-[11px] font-bold text-[#2c6b7f]">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <Link to={docLink(r.doc.id)} className="block truncate font-medium text-slate-700 hover:text-[#357f96]">{r.doc.title}</Link>
                    <div className="text-xs text-slate-400">{r.views} lượt xem · {r.downloads} lượt tải</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
      <div className="mt-2 text-xs text-slate-400">Các nhóm thời gian đọc: {READ_BUCKETS.join(' · ')}.</div>
    </>
  );
}
